import React, { useMemo, useState } from "react";
import ArrowLeftToRight from "../../../image/icons/map/details/ArrowLeftToRight.svg?react";
import Clock from "../../../image/icons/map/details/ClockIcon.svg?react";
import Dot from "../../../image/icons/map/details/Dot.svg?react";
import { rawGpsDataService } from "../../../services/navigation";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { formatTime } from "../../../tools/TimeRelated";
import { type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ContainerProps } from "../../../types/ItineraryPlanner";
import {
    buildItineraryListCardStyle,
    ITINERARY_LIST_CARD_CHROME_CLASSNAME,
} from "../itineraryList/itineraryListCard.styles";
import TravelProgress from "../itineraryList/TravelProgress";
import CarPriceDetail from "./CarPriceDetail";
import {
    buildItineraryPricePillChromeLoadingStyle,
    buildItineraryPricePillChromeOutlineStyle,
    ITINERARY_PRICE_PILL_CHROME_BASE_CLASSNAME,
    ITINERARY_PRICE_PILL_CHROME_OUTLINE_MY_CAR_CLASSNAME,
} from "./itineraryPricePill.styles";
import PriceConversion from "./PriceConversion";
import TollPriceDisplay from "./TollPriceDisplay";

interface CarItineraryData {
    "duration"?: number;
    "startTime": string | number | Date;
    "endTime":   string | number | Date;
    "legs":      ItineraryLegDTO[];
}

interface MyCarPropositionProps {
    "selectedInputs": ContainerProps["selectedInputs"];

    /** Wear cost per km (CHF) from pricing slice */
    "wearCostPerKmChf":     number;
    "ITakeMyCarData":       CarItineraryData;
    "setITakeMyCar":        React.Dispatch<React.SetStateAction<boolean>>;
    "onResetPanelExpanded": () => void;
    "isEuro":               boolean;
    "isCarPriceOpen":       boolean;
    "setIsCarPriceOpen":    React.Dispatch<React.SetStateAction<boolean>>;
    "isDarkUi":             boolean;
    "carDetails":        {
        "tollPrice":  number;
        "distance":   number;
        "totalPrice": number;
    } | null;
    "setCarDetails":     React.Dispatch<React.SetStateAction<{
        "tollPrice":  number;
        "distance":   number;
        "totalPrice": number;
    } | null>>;
}

/**
 * Personal-car proposition row in the itinerary list, styled like list itinerary cards.
 *
 * @param props Component props.
 * @returns MyCar list card, or `null` when itinerary data is incomplete.
 */
const MyCarProposition: React.FC<MyCarPropositionProps> = ( {
    selectedInputs,
    wearCostPerKmChf,
    ITakeMyCarData,
    setITakeMyCar,
    onResetPanelExpanded,
    isEuro,
    isCarPriceOpen,
    setIsCarPriceOpen,
    isDarkUi,
    carDetails,
    setCarDetails,
} ) => {
    const [ tollPrice, setTollPrice ] = useState<number | null>( null );
    const palette = getUIColorPalette( isDarkUi );
    const cardStyle = useMemo(
        (): React.CSSProperties => buildItineraryListCardStyle( palette, isDarkUi ),
        [ isDarkUi, palette ],
    );

    if ( ITakeMyCarData === null ) {
        return null;
    }

    if ( ITakeMyCarData.duration === undefined ) {
        return null;
    }

    const formattedDuration: string = formatTime( ITakeMyCarData.duration );
    const myCarPricePillAccent = palette.myCarPricePillAccent;
    const hasResolvedPrice = tollPrice !== null;
    const pricePillClassName = hasResolvedPrice
        ? `ml-auto cursor-pointer ${ ITINERARY_PRICE_PILL_CHROME_OUTLINE_MY_CAR_CLASSNAME }`
        : `ml-auto cursor-pointer ${ ITINERARY_PRICE_PILL_CHROME_BASE_CLASSNAME }`;
    const pricePillStyle = hasResolvedPrice
        ? buildItineraryPricePillChromeOutlineStyle( myCarPricePillAccent )
        : buildItineraryPricePillChromeLoadingStyle( myCarPricePillAccent );

    return (
        <div
            className={`${ ITINERARY_LIST_CARD_CHROME_CLASSNAME } mt-[5px] rounded-xl cursor-pointer`}
            onClick={() => {
                setITakeMyCar( true );
                onResetPanelExpanded();
            }}
            style={cardStyle}
        >
            <div className="flex items-center">
                <div className="flex justify-center items-center">
                    <Clock
                        aria-label="Petite horloge"
                        className="inline-block align-middle shrink-0 w-[16px] h-[16px]"
                        style={{ "color": "var(--itinerary-text-secondary, #334155)" }}
                    />
                    <h4
                        className="pl-2 text-xs flex items-center"
                        style={{ "color": "var(--itinerary-text-primary, #0f172a)" }}
                    >
                        {formattedDuration}
                        <Dot
                            className="ml-1 mr-1 inline-block align-middle self-center"
                            style={{ "color": "var(--itinerary-text-secondary, #334155)" }}
                        />
                        <b>
                            {new Date( ITakeMyCarData.startTime ).toLocaleTimeString( [], {
                                "hour":   "2-digit",
                                "minute": "2-digit",
                                "hour12": false,
                            } )}
                        </b>
                        <ArrowLeftToRight
                            className="ml-1 mr-1 w-3 inline-block align-middle self-center"
                            style={{ "color": "var(--itinerary-text-secondary, #334155)" }}
                        />
                        {new Date( ITakeMyCarData.endTime ).toLocaleTimeString( [], {
                            "hour":   "2-digit",
                            "minute": "2-digit",
                            "hour12": false,
                        } )}
                    </h4>
                </div>
                <TollPriceDisplay
                    fromLat={selectedInputs.departure.lat === -1 ? rawGpsDataService.getLastValue().latitude : selectedInputs.departure.lat}
                    fromLng={selectedInputs.departure.lon === -1 ? rawGpsDataService.getLastValue().longitude : selectedInputs.departure.lon}
                    onDetailsCalculated={setCarDetails}
                    onPriceCalculated={setTollPrice}
                    toLat={selectedInputs.arrival.lat === -1 ? rawGpsDataService.getLastValue().latitude : selectedInputs.arrival.lat}
                    toLng={selectedInputs.arrival.lon === -1 ? rawGpsDataService.getLastValue().longitude : selectedInputs.arrival.lon}
                    wearCostPerKmChf={wearCostPerKmChf}
                />
                <div
                    className={pricePillClassName}
                    onClick={( e ) => {
                        e.stopPropagation();
                        setIsCarPriceOpen( true );
                    }}
                    style={pricePillStyle}
                >
                    {hasResolvedPrice
                        ? (
                            <PriceConversion
                                chooseEuro={isEuro}
                                className="text-inherit"
                                compact={true}
                                price={tollPrice}
                            />
                        )
                        : (
                            <span className="inline-flex items-center gap-1">
                                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                {" "}
                                CHF
                            </span>
                        )}
                </div>
            </div>
            <div className="relative z-0 mt-1.5 min-w-0">
                <TravelProgress
                    isMyCar={true}
                    legs={ITakeMyCarData.legs}
                />
            </div>
            <CarPriceDetail
                distance={carDetails?.distance ?? 0}
                isDarkUi={isDarkUi}
                isEuro={isEuro}
                isOpen={isCarPriceOpen}
                onClose={() => {
                    setIsCarPriceOpen( false );
                }}
                tollPrice={carDetails?.tollPrice ?? 0}
            />
        </div>
    );
};

export default MyCarProposition;
